import { useState, useEffect, useMemo } from 'react';
import { X, Youtube, Check, AlertTriangle, Loader, Link as LinkIcon, Settings, Filter, Clock, History, Smartphone } from 'lucide-react';
import { Link } from 'react-router-dom';

interface YouTubeImporterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImportComplete: () => void;
  token: string;
}

interface FetchedVideo {
  id: string;
  title: string;
  description: string;
  thumbnail: string;
  author: string;
  channelId?: string;
  selected: boolean;
  publishedAt?: string;
  duration?: string;
  isShort?: boolean;
}

interface ExistingStory {
  id: string;
  title: string;
  video_id?: string;
  youtube_url?: string;
  source?: string;
}

import { API_URL } from '../../config/api';

export default function YouTubeImporterModal({ isOpen, onClose, onImportComplete, token }: YouTubeImporterModalProps) {
  const [url, setUrl] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [videos, setVideos] = useState<FetchedVideo[]>([]);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [existingVideos, setExistingVideos] = useState<Set<string>>(new Set());
  const [duplicatesFound, setDuplicatesFound] = useState(0);
  const [shortsSkipped, setShortsSkipped] = useState(0);
  const [showDuplicates, setShowDuplicates] = useState(false);

  // Default values form state
  const [category, setCategory] = useState('');
  const [isKidsMode, setIsKidsMode] = useState(false);
  const [isPremium, setIsPremium] = useState(false);
  const [status, setStatus] = useState('published');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [country, setCountry] = useState('MA');
  const [originalLanguage, setOriginalLanguage] = useState('ar');
  const [genres, setGenres] = useState<string[]>([]);
  const [categoriesList, setCategoriesList] = useState<any[]>([]);
  const [genresList, setGenresList] = useState<any[]>([]);
  const [selectedChannel, setSelectedChannel] = useState<string>('all');

  // جلب الفيديوهات الموجودة مسبقاً
  useEffect(() => {
    if (isOpen) {
      fetchExistingVideos();
      fetchAiSettings();
      fetchCategories();
      fetchGenres();
    } else {
      setUrl('');
      setVideos([]);
      setError('');
      setProgress(0);
      setDuplicatesFound(0);
      setShortsSkipped(0);
      setExistingVideos(new Set());
    }
  }, [isOpen]);

  const fetchExistingVideos = async () => {
    try {
      const res = await fetch(`${API_URL}/content/app/stories?app_target=stories&limit=500&_t=${Date.now()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        const stories = data?.data || data || [];
        
        const videoIds = new Set<string>();
        stories.forEach((story: any) => {
          if (story.video_id) {
            videoIds.add(story.video_id);
          }
          if (story.youtube_url) {
            const match = story.youtube_url.match(/(?:v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
            if (match) {
              videoIds.add(match[1]);
            }
          }
          if (story.media?.video_id) {
            videoIds.add(story.media.video_id);
          }
          if (story.source?.includes('youtube') || story.source?.includes('yt')) {
            const match = story.source.match(/(?:v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
            if (match) {
              videoIds.add(match[1]);
            }
          }
        });
        
        setExistingVideos(videoIds);
        console.log(`📚 Found ${videoIds.size} existing videos`);
      }
    } catch (e) {
      console.error('Failed to fetch existing videos:', e);
    }
  };

  const fetchAiSettings = async () => {
    try {
      const res = await fetch(`${API_URL}/settings/ai`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.youtube_api_key) {
          setApiKey(data.youtube_api_key);
          localStorage.setItem('YOUTUBE_API_KEY', data.youtube_api_key);
        }
      }
    } catch (e) {}

    const key = localStorage.getItem('YOUTUBE_API_KEY');
    if (key) {
      setApiKey(key);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await fetch(`${API_URL}/content/categories?app_target=stories`);
      if (res.ok) setCategoriesList(await res.json());
    } catch (e) {}
  };

  const fetchGenres = async () => {
    try {
      const res = await fetch(`${API_URL}/content/genres?app_target=stories`);
      if (res.ok) setGenresList(await res.json());
    } catch (e) {}
  };

  const extractId = (urlStr: string) => {
    try {
      const u = new URL(urlStr);
      if (u.searchParams.has('list')) {
        return { type: 'playlist', id: u.searchParams.get('list') };
      }
      if (u.searchParams.has('v')) {
        return { type: 'video', id: u.searchParams.get('v') };
      }
      if (u.hostname === 'youtu.be') {
        return { type: 'video', id: u.pathname.substring(1) };
      }
      if (u.hostname === 'www.youtube.com' && u.pathname.startsWith('/@')) {
        return { type: 'channel', id: u.pathname.substring(2) };
      }
      if (u.hostname === 'www.youtube.com' && u.pathname.startsWith('/c/')) {
        return { type: 'channel', id: u.pathname.substring(3) };
      }
      if (u.hostname === 'www.youtube.com' && u.pathname.startsWith('/channel/')) {
        return { type: 'channel', id: u.pathname.substring(9) };
      }
      return null;
    } catch {
      return null;
    }
  };

  // الكشف عن فيديوهات Shorts
  const isShortVideo = (videoId: string, title: string = '', description: string = ''): boolean => {
    // 1. التحقق من طول الفيديو (يتم جلب المدة من API)
    // 2. التحقق من وجود #shorts في الوصف أو العنوان
    const hasShortTag = 
      title.toLowerCase().includes('#shorts') || 
      description.toLowerCase().includes('#shorts') ||
      title.toLowerCase().includes('shorts') ||
      description.toLowerCase().includes('shorts');
    
    if (hasShortTag) return true;
    
    // 3. التحقق من نسبة العرض إلى الارتفاع (سيتم التحقق لاحقاً)
    return false;
  };

  // جلب مدة الفيديو من YouTube API
  const fetchVideoDuration = async (videoId: string): Promise<number | null> => {
    try {
      const res = await fetch(`https://www.googleapis.com/youtube/v3/videos?part=contentDetails&id=${videoId}&key=${apiKey}`);
      const data = await res.json();
      if (data.items && data.items.length > 0) {
        const duration = data.items[0].contentDetails.duration;
        // تحويل مدة ISO 8601 إلى ثواني
        const match = duration.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
        if (match) {
          const hours = parseInt(match[1] || '0');
          const minutes = parseInt(match[2] || '0');
          const seconds = parseInt(match[3] || '0');
          return hours * 3600 + minutes * 60 + seconds;
        }
      }
      return null;
    } catch (e) {
      console.error('Failed to fetch video duration:', e);
      return null;
    }
  };

  // جلب فيديوهات القناة
  const fetchChannelVideos = async (channelId: string, channelName?: string) => {
    let allItems: FetchedVideo[] = [];
    let nextPageToken = '';
    let pageCount = 0;
    const maxPages = 5;
    let shortsCount = 0;
    
    try {
      let channelTitle = channelName || '';
      if (!channelTitle) {
        const channelRes = await fetch(`https://www.googleapis.com/youtube/v3/channels?part=snippet&id=${channelId}&key=${apiKey}`);
        const channelData = await channelRes.json();
        if (channelData.items && channelData.items.length > 0) {
          channelTitle = channelData.items[0].snippet.title;
        }
      }

      do {
        const res = await fetch(
          `https://www.googleapis.com/youtube/v3/search?part=snippet&channelId=${channelId}&maxResults=50&order=date&type=video&key=${apiKey}${nextPageToken ? `&pageToken=${nextPageToken}` : ''}`
        );
        const data = await res.json();
        
        if (data.error) throw new Error(data.error.message);
        
        if (data.items) {
          const videoItems = data.items.filter((item: any) => 
            item.id && item.id.videoId && 
            item.snippet && 
            item.snippet.title !== 'Private video' && 
            item.snippet.title !== 'Deleted video'
          );
          
          // جلب مدد الفيديوهات دفعة واحدة
          const videoIds = videoItems.map((item: any) => item.id.videoId);
          const durationsMap = new Map<string, number>();
          
          // جلب مدد الفيديوهات (مقسم إلى مجموعات من 50)
          for (let i = 0; i < videoIds.length; i += 50) {
            const batch = videoIds.slice(i, i + 50);
            const durationRes = await fetch(
              `https://www.googleapis.com/youtube/v3/videos?part=contentDetails&id=${batch.join(',')}&key=${apiKey}`
            );
            const durationData = await durationRes.json();
            if (durationData.items) {
              durationData.items.forEach((item: any) => {
                const duration = item.contentDetails.duration;
                const match = duration.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
                if (match) {
                  const hours = parseInt(match[1] || '0');
                  const minutes = parseInt(match[2] || '0');
                  const seconds = parseInt(match[3] || '0');
                  durationsMap.set(item.id, hours * 3600 + minutes * 60 + seconds);
                }
              });
            }
          }
          
          for (const item of videoItems) {
            const videoId = item.id.videoId;
            const duration = durationsMap.get(videoId) || 0;
            
            // التحقق من كون الفيديو Short (مدة أقل من 60 ثانية)
            const isShort = duration < 60 || isShortVideo(videoId, item.snippet.title, item.snippet.description);
            
            if (isShort) {
              shortsCount++;
              continue; // تخطي فيديوهات Shorts
            }
            
            if (!existingVideos.has(videoId)) {
              allItems.push({
                id: videoId,
                title: item.snippet.title,
                description: item.snippet.description || '',
                thumbnail: item.snippet.thumbnails?.maxres?.url || 
                           item.snippet.thumbnails?.high?.url || 
                           item.snippet.thumbnails?.default?.url || '',
                author: channelTitle || item.snippet.channelTitle,
                channelId: channelId,
                selected: true,
                publishedAt: item.snippet.publishedAt,
                duration: duration > 0 ? `${Math.floor(duration / 60)}:${String(duration % 60).padStart(2, '0')}` : undefined,
                isShort: false
              });
            } else {
              setDuplicatesFound(prev => prev + 1);
            }
          }
        }
        
        nextPageToken = data.nextPageToken;
        pageCount++;
      } while (nextPageToken && pageCount < maxPages);
      
      setShortsSkipped(shortsCount);
      return allItems;
    } catch (err: any) {
      throw new Error(err.message || 'Failed to fetch channel videos');
    }
  };

  // جلب فيديوهات القائمة
  const fetchPlaylistVideos = async (playlistId: string) => {
    let allItems: FetchedVideo[] = [];
    let nextPageToken = '';
    let pageCount = 0;
    const maxPages = 10;
    let shortsCount = 0;
    
    try {
      do {
        const res = await fetch(
          `https://www.googleapis.com/youtube/v3/playlistItems?part=snippet&maxResults=50&playlistId=${playlistId}&key=${apiKey}${nextPageToken ? `&pageToken=${nextPageToken}` : ''}`
        );
        const data = await res.json();
        
        if (data.error) throw new Error(data.error.message);
        
        if (data.items) {
          // جمع معرفات الفيديوهات
          const videoItems = data.items.filter((item: any) => 
            item.snippet && 
            item.snippet.title !== 'Private video' && 
            item.snippet.title !== 'Deleted video'
          );
          
          const videoIds = videoItems.map((item: any) => item.snippet.resourceId.videoId);
          const durationsMap = new Map<string, number>();
          
          // جلب مدد الفيديوهات
          for (let i = 0; i < videoIds.length; i += 50) {
            const batch = videoIds.slice(i, i + 50);
            const durationRes = await fetch(
              `https://www.googleapis.com/youtube/v3/videos?part=contentDetails&id=${batch.join(',')}&key=${apiKey}`
            );
            const durationData = await durationRes.json();
            if (durationData.items) {
              durationData.items.forEach((item: any) => {
                const duration = item.contentDetails.duration;
                const match = duration.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
                if (match) {
                  const hours = parseInt(match[1] || '0');
                  const minutes = parseInt(match[2] || '0');
                  const seconds = parseInt(match[3] || '0');
                  durationsMap.set(item.id, hours * 3600 + minutes * 60 + seconds);
                }
              });
            }
          }
          
          for (const item of videoItems) {
            const snippet = item.snippet;
            const videoId = snippet.resourceId.videoId;
            const duration = durationsMap.get(videoId) || 0;
            
            // التحقق من كون الفيديو Short
            const isShort = duration < 60 || isShortVideo(videoId, snippet.title, snippet.description);
            
            if (isShort) {
              shortsCount++;
              continue;
            }
            
            if (!existingVideos.has(videoId)) {
              allItems.push({
                id: videoId,
                title: snippet.title,
                description: snippet.description || '',
                thumbnail: snippet.thumbnails?.maxres?.url || 
                           snippet.thumbnails?.high?.url || 
                           snippet.thumbnails?.default?.url || '',
                author: snippet.videoOwnerChannelTitle || snippet.channelTitle,
                channelId: snippet.channelId,
                selected: true,
                publishedAt: snippet.publishedAt,
                duration: duration > 0 ? `${Math.floor(duration / 60)}:${String(duration % 60).padStart(2, '0')}` : undefined,
                isShort: false
              });
            } else {
              setDuplicatesFound(prev => prev + 1);
            }
          }
        }
        
        nextPageToken = data.nextPageToken;
        pageCount++;
      } while (nextPageToken && pageCount < maxPages);
      
      setShortsSkipped(shortsCount);
      return allItems;
    } catch (err: any) {
      throw new Error(err.message || 'Failed to fetch playlist videos');
    }
  };

  // جلب فيديو فردي
  const fetchSingleVideo = async (videoId: string) => {
    try {
      // جلب معلومات الفيديو
      const res = await fetch(`https://www.googleapis.com/youtube/v3/videos?part=snippet,contentDetails&id=${videoId}&key=${apiKey}`);
      const data = await res.json();
      
      if (data.error) throw new Error(data.error.message);
      
      if (data.items && data.items.length > 0) {
        const item = data.items[0];
        const snippet = item.snippet;
        const videoIdFromData = item.id;
        const contentDetails = item.contentDetails;
        
        // حساب مدة الفيديو
        const duration = contentDetails.duration;
        const match = duration.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
        let durationSeconds = 0;
        if (match) {
          const hours = parseInt(match[1] || '0');
          const minutes = parseInt(match[2] || '0');
          const seconds = parseInt(match[3] || '0');
          durationSeconds = hours * 3600 + minutes * 60 + seconds;
        }
        
        // التحقق من كون الفيديو Short
        const isShort = durationSeconds < 60 || isShortVideo(videoIdFromData, snippet.title, snippet.description);
        
        if (isShort) {
          setShortsSkipped(1);
          return [];
        }
        
        if (existingVideos.has(videoIdFromData)) {
          setDuplicatesFound(1);
          return [];
        }
        
        return [{
          id: videoIdFromData,
          title: snippet.title,
          description: snippet.description || '',
          thumbnail: snippet.thumbnails?.maxres?.url || 
                     snippet.thumbnails?.high?.url || 
                     snippet.thumbnails?.default?.url || '',
          author: snippet.channelTitle,
          channelId: snippet.channelId,
          selected: true,
          publishedAt: snippet.publishedAt,
          duration: durationSeconds > 0 ? `${Math.floor(durationSeconds / 60)}:${String(durationSeconds % 60).padStart(2, '0')}` : undefined,
          isShort: false
        }];
      }
      return [];
    } catch (err: any) {
      throw new Error(err.message || 'Failed to fetch video');
    }
  };

  const fetchYouTubeData = async () => {
    if (!apiKey) {
      setError('YouTube API Key is missing. Please configure it in Settings > AI Integrations.');
      return;
    }

    const info = extractId(url);
    if (!info) {
      setError('Invalid YouTube URL. Please provide a valid video, playlist, or channel link.');
      return;
    }

    setLoading(true);
    setError('');
    setVideos([]);
    setDuplicatesFound(0);
    setShortsSkipped(0);

    try {
      let fetchedVideos: FetchedVideo[] = [];
      
      if (info.type === 'video') {
        fetchedVideos = await fetchSingleVideo(info.id as string);
      } else if (info.type === 'playlist') {
        fetchedVideos = await fetchPlaylistVideos(info.id as string);
      } else if (info.type === 'channel') {
        fetchedVideos = await fetchChannelVideos(info.id as string);
      }
      
      if (fetchedVideos.length > 0) {
        setVideos(fetchedVideos);
        const channels = [...new Set(fetchedVideos.map(v => v.channelId || v.author))];
        if (channels.length > 1) {
          setSelectedChannel('all');
        }
      } else if (duplicatesFound > 0 && shortsSkipped === 0) {
        setError(`All ${duplicatesFound} videos were already imported. No new videos found.`);
      } else if (shortsSkipped > 0 && duplicatesFound === 0 && fetchedVideos.length === 0) {
        setError(`Skipped ${shortsSkipped} Shorts videos. No regular videos found.`);
      } else {
        setError('No videos found in this source.');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch YouTube data.');
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    const selectedVideos = videos.filter(v => v.selected);
    if (selectedVideos.length === 0) {
      setError('Please select at least one video to import.');
      return;
    }

    setImporting(true);
    setProgress(0);
    setError('');

    let successCount = 0;
    let failCount = 0;

    for (let i = 0; i < selectedVideos.length; i++) {
      const v = selectedVideos[i];
      let audioUrl = `https://utube.uplaytv3117.workers.dev/?v=${v.id}`;
      if (startTime) audioUrl += `&t=${startTime}`;
      if (endTime) audioUrl += `&end=${endTime}`;

      const payload = {
        app_target: 'stories',
        type: 'Single/Movie',
        is_kids_mode: isKidsMode,
        title: v.title,
        synopsis: v.description,
        video_id: v.id,
        youtube_url: `https://www.youtube.com/watch?v=${v.id}`,
        source: `youtube:${v.id}`,
        translations: {
          'ar': { 
            title: v.title, 
            synopsis: v.description || '',
            audio_url: audioUrl,
            video_url: `https://www.youtube.com/watch?v=${v.id}`
          }
        },
        category,
        author: v.author,
        status,
        genres: genres.join(','),
        country,
        original_language: originalLanguage,
        poster_image: v.thumbnail,
        is_premium: isPremium,
        movie_details: {
          youtube_id: v.id,
          channel: v.author,
          channel_id: v.channelId,
          published_at: v.publishedAt,
          duration: v.duration
        }
      };

      try {
        const response = await fetch(`${API_URL}/content/story`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(payload)
        });

        if (response.ok) {
          successCount++;
          existingVideos.add(v.id);
        } else {
          failCount++;
        }
      } catch (err) {
        console.error('Failed to import video:', v.id, err);
        failCount++;
      }
      setProgress(Math.round(((i + 1) / selectedVideos.length) * 100));
    }

    setImporting(false);
    
    if (successCount > 0) {
      onImportComplete();
      setVideos(prev => prev.filter(v => !v.selected || existingVideos.has(v.id)));
      setTimeout(() => {
        if (videos.filter(v => !existingVideos.has(v.id)).length === 0) {
          onClose();
        }
      }, 1500);
    } else {
      setError(`Failed to import any videos. ${failCount} failed.`);
    }
  };

  const toggleSelectAll = () => {
    const allSelected = videos.every(v => v.selected);
    setVideos(videos.map(v => ({ ...v, selected: !allSelected })));
  };

  const toggleVideo = (id: string) => {
    setVideos(videos.map(v => v.id === id ? { ...v, selected: !v.selected } : v));
  };

  const filteredVideos = useMemo(() => {
    if (selectedChannel === 'all') return videos;
    return videos.filter(v => v.channelId === selectedChannel || v.author === selectedChannel);
  }, [videos, selectedChannel]);

  const uniqueChannels = useMemo(() => {
    const channels = new Map<string, string>();
    videos.forEach(v => {
      const id = v.channelId || v.author;
      if (!channels.has(id)) {
        channels.set(id, v.author);
      }
    });
    return Array.from(channels.entries()).map(([id, name]) => ({ id, name }));
  }, [videos]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 font-arabic" dir="rtl">
      <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-100 bg-slate-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-red-100 text-red-600 flex items-center justify-center">
              <Youtube size={24} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-800">جلب القصص من يوتيوب</h2>
              <p className="text-sm text-slate-500">جلب فيديو واحد، قائمة تشغيل، أو قناة كاملة (يتخطى الـ Shorts تلقائياً)</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-200 rounded-full transition-colors">
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 flex-1 overflow-y-auto">
          {!apiKey && (
            <div className="mb-6 p-4 bg-orange-50 border border-orange-200 rounded-lg flex items-start gap-3">
              <AlertTriangle className="text-orange-500 shrink-0 mt-0.5" size={20} />
              <div>
                <h3 className="font-semibold text-orange-800 text-sm">تنبيه: مفتاح YouTube API مفقود</h3>
                <p className="text-sm text-orange-600 mt-1 mb-2">لجلب البيانات بشكل صحيح، تحتاج إلى إعداد مفتاح API الخاص بيوتيوب.</p>
                <Link to="/settings" className="inline-flex items-center gap-1 text-sm font-medium text-indigo-600 hover:text-indigo-700 bg-white px-3 py-1.5 border border-orange-200 rounded-md">
                  <Settings size={16} /> الذهاب للإعدادات
                </Link>
              </div>
            </div>
          )}

          <div className="flex gap-2 mb-6">
            <div className="relative flex-1">
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                <LinkIcon size={18} className="text-slate-400" />
              </div>
              <input
                type="text"
                placeholder="https://www.youtube.com/watch?v=... أو قائمة تشغيل أو قناة..."
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                dir="ltr"
                className="w-full pr-10 pl-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent text-left"
              />
            </div>
            <button
              onClick={fetchYouTubeData}
              disabled={loading || !url}
              className="px-6 py-3 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors flex items-center gap-2"
            >
              {loading ? <Loader className="animate-spin" size={18} /> : <Youtube size={18} />}
              جلب الداتا
            </button>
          </div>

          {error && (
            <div className={`text-sm mb-4 p-3 rounded-lg ${error.includes('already imported') || error.includes('Skipped') ? 'bg-amber-50 text-amber-700 border border-amber-200' : 'text-red-500'}`}>
              {error}
            </div>
          )}

          {videos.length > 0 && (
            <div className="space-y-6">
              {/* Summary Stats */}
              <div className="flex flex-wrap gap-3">
                {duplicatesFound > 0 && (
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-full">
                    <History size={14} className="text-amber-500" />
                    <span className="text-xs text-amber-700">مكرر: {duplicatesFound}</span>
                  </div>
                )}
                {shortsSkipped > 0 && (
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-full">
                    <Smartphone size={14} className="text-blue-500" />
                    <span className="text-xs text-blue-700">Shorts: {shortsSkipped} (تم التخطي)</span>
                  </div>
                )}
                <div className="flex items-center gap-2 px-3 py-1.5 bg-green-50 border border-green-200 rounded-full">
                  <Check size={14} className="text-green-500" />
                  <span className="text-xs text-green-700">جديد: {videos.length}</span>
                </div>
              </div>

              {/* Channel Filter */}
              {uniqueChannels.length > 1 && (
                <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg border border-slate-200">
                  <Filter size={16} className="text-slate-500" />
                  <span className="text-sm text-slate-600 font-medium">تصفية حسب القناة:</span>
                  <select 
                    value={selectedChannel} 
                    onChange={(e) => setSelectedChannel(e.target.value)}
                    className="text-sm border border-slate-300 rounded-md px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="all">جميع القنوات ({videos.length})</option>
                    {uniqueChannels.map(ch => (
                      <option key={ch.id} value={ch.id}>
                        {ch.name} ({videos.filter(v => v.channelId === ch.id || v.author === ch.id).length})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Global Settings for Import */}
              <div className="bg-slate-50 p-5 rounded-xl border border-slate-200">
                <h3 className="font-semibold text-slate-800 mb-4">الإعدادات الافتراضية للاستيراد</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">القسم (Category)</label>
                    <select value={category} onChange={(e) => setCategory(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm">
                      <option value="">اختيار القسم...</option>
                      {categoriesList.map(c => (
                        <option key={c.uuid} value={c.name}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">الأنواع (Genres)</label>
                    <select multiple value={genres} onChange={(e) => setGenres(Array.from(e.target.selectedOptions, option => option.value))} className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm" size={3}>
                      {genresList.map(g => (
                        <option key={g.uuid} value={g.name}>{g.name}</option>
                      ))}
                    </select>
                    <p className="text-xs text-slate-500 mt-1">اضغط Ctrl لتحديد أكثر من نوع</p>
                  </div>
                  <div className="space-y-4">
                    <label className="flex items-center gap-2 cursor-pointer mt-6">
                      <input type="checkbox" checked={isPremium} onChange={(e) => setIsPremium(e.target.checked)} className="w-4 h-4 text-indigo-600 rounded border-slate-300 focus:ring-indigo-500" />
                      <span className="text-sm font-medium text-slate-700">قصة مميزة (Premium)</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={isKidsMode} onChange={(e) => setIsKidsMode(e.target.checked)} className="w-4 h-4 text-indigo-600 rounded border-slate-300 focus:ring-indigo-500" />
                      <span className="text-sm font-medium text-slate-700">وضع الأطفال (Kids Mode)</span>
                    </label>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">الحالة (Status)</label>
                    <select value={status} onChange={(e) => setStatus(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm">
                      <option value="published">Published</option>
                      <option value="draft">Draft</option>
                      <option value="archived">Archived</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">الدولة (Country)</label>
                    <input type="text" value={country} onChange={(e) => setCountry(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm" placeholder="MA" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">اللغة الأصلية (Original Language)</label>
                    <input type="text" value={originalLanguage} onChange={(e) => setOriginalLanguage(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm" placeholder="ar" />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">وقت بداية الصوت (ثواني)</label>
                    <input type="number" placeholder="مثال: 82" value={startTime} onChange={(e) => setStartTime(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm" />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 bg-slate-50 border-t border-slate-200">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-200 rounded-md transition-colors">إلغاء</button>
          {step === 'select' && (
            <button type="button" onClick={handleProcessAudio} disabled={loading || (!videoUrl && !selectedPlaylists.some(p => p.selected))} className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md transition-colors disabled:opacity-50 flex items-center gap-2">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              استيراد وحفظ
            </button>
          )}
        </div>
      </div>
    </div>
  );
}